// src/pages/parts/orderPickup/components/OtherInvoiceDialog.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { useGetExternalInvoiceDetailQuery, useExternalOrderPickupMutation, useGetExternalItemDescriptionsQuery } from '../../../../api/endpoints/parts';
import { useGetPreAccountingKdvQuery } from '../../../../api/endpoints/preAccounting';
import { useGetCustomerByIdQuery } from '../../../../api/customerApi';
import { useLazyGetCustomerDiscountRatesQuery } from '../../../../api';
import { toastService } from '../../../../utils/toastService';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { usePreAccountingCustomerOptions } from '../../../../hooks/usePreAccountingCustomerOptions';

const emptyHeader = {
    invoiceNo: '',
    invoiceDate: null,
    invoiceAmount: null,
    wareHouseId: null,
    currency: null
};

export default function OtherInvoiceDialog({ visible, onHide, invoiceNo, onSuccess }) {
    const isEdit = !!invoiceNo;
    const { getCurrentLanguageId } = useLanguage();
    var languageId = getCurrentLanguageId();
    const browserLocale = navigator.language || 'tr-TR';
    if (languageId !== 1 && languageId !== 2) {
        languageId = 1;
    }
    const { th } = useLanguage();
    const { data: detailResp, isFetching, refetch: refetchDetail } = useGetExternalInvoiceDetailQuery(isEdit ? { LanguageId: languageId, InvoiceNo: invoiceNo } : { LanguageId: 2 }, { skip: !visible });

    const [saveExternal, { isLoading: isSaving }] = useExternalOrderPickupMutation();

    const [partFilterValue, setPartFilterValue] = useState('');
    const [partSearchText, setPartSearchText] = useState('');
    const debounceTimerRef = React.useRef(null);

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, []);

    // Fetch external item descriptions for part dropdown
    const { data: externalItemsData, isFetching: isFetchingParts } = useGetExternalItemDescriptionsQuery({ IsActive: 1, pageSize: 50, pageNumber: 1, LanguageId: languageId, searchText: partSearchText }, { skip: !visible });

    // Customer search state
    const [customerSearchText, setCustomerSearchText] = useState('');
    const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState('');
    const [customerFilterValue, setCustomerFilterValue] = useState('');

    // Debounce customer search - 600ms delay
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedCustomerSearch(customerSearchText);
        }, 600);

        return () => clearTimeout(timer);
    }, [customerSearchText]);

    // Fetch customers for dropdown
    const { customerOptions: fetchedCustomerOptions, isFetching: isFetchingCustomers } = usePreAccountingCustomerOptions({ 
        SearchText: debouncedCustomerSearch,
        skip: !visible 
    });

    // Customer loaded by ID (for edit mode) - stored as state to trigger re-render
    const [customerFromId, setCustomerFromId] = useState(null);
    
    const customerOptions = useMemo(() => {
        let options = [];
        
        // Add customer loaded by ID first (edit mode)
        if (customerFromId) {
            options.push(customerFromId);
        }
        
        // Merge with fetched options
        if (fetchedCustomerOptions && fetchedCustomerOptions.length > 0) {
            // Add fetched options that are not already in the list
            fetchedCustomerOptions.forEach(opt => {
                if (!options.some(o => o.value === opt.value)) {
                    options.push(opt);
                }
            });
        }
        
        return options;
    }, [fetchedCustomerOptions, customerFromId]);

    // Fetch KDV rate
    const { data: kdvRate } = useGetPreAccountingKdvQuery(undefined, { skip: !visible });

    const [selectedPartId, setSelectedPartId] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    
    // Track customerId from API for edit mode - to fetch customer by ID
    const [editCustomerId, setEditCustomerId] = useState(null);
    
    // Customer discount rates
    const [getCustomerDiscountRates] = useLazyGetCustomerDiscountRatesQuery();
    const [currentDiscountRate, setCurrentDiscountRate] = useState(0);
    
    // Fetch customer by ID when in edit mode (to populate dropdown with existing customer)
    const { data: customerByIdData } = useGetCustomerByIdQuery(editCustomerId, { skip: !editCustomerId });
    
    // Add customer from ID query to options and select it
    useEffect(() => {
        if (customerByIdData && editCustomerId) {
            // Combine name + surname for label
            const namePart = customerByIdData.name || '';
            const surnamePart = customerByIdData.surname && customerByIdData.surname !== '-' ? customerByIdData.surname : '';
            const fullName = `${namePart} ${surnamePart}`.trim() || `Müşteri #${editCustomerId}`;
            
            const customerOption = {
                label: fullName,
                value: customerByIdData.id
            };
            
            // Set customer option (triggers re-render and updates customerOptions)
            setCustomerFromId(customerOption);
            
            // Set selected customer to this ID
            setSelectedCustomer(customerByIdData.id);
            
            // Fetch discount rates for the customer
            fetchAndApplyDiscountRates(customerByIdData.id);
        }
    }, [customerByIdData, editCustomerId]);
    
    // Fetch and apply discount rates when customer changes
    const fetchAndApplyDiscountRates = async (customerId) => {
        if (!customerId) {
            setCurrentDiscountRate(0);
            return;
        }
        
        try {
            const result = await getCustomerDiscountRates(Number(customerId)).unwrap();
            if (result) {
                const discountRate = result.partsDiscountRate || 0;
                setCurrentDiscountRate(discountRate);
                
                // Apply discount to existing parts
                setParts(prev => prev.map(part => ({
                    ...part,
                    discountRate: discountRate,
                    discountedPrice: calculateDiscountedPrice(part.price, discountRate)
                })));
            }
        } catch (error) {
            console.error('Discount rates fetch error:', error);
            setCurrentDiscountRate(0);
        }
    };
    
    // Calculate discounted price
    const calculateDiscountedPrice = (originalPrice, discountRate) => {
        if (!originalPrice || !discountRate) return originalPrice;
        return originalPrice - (originalPrice * discountRate / 100);
    };
    
    // Handle customer change
    const handleCustomerChange = async (newCustomerId) => {
        setSelectedCustomer(newCustomerId);
        setErrors(prev => ({ ...prev, customer: null }));
        
        if (newCustomerId) {
            await fetchAndApplyDiscountRates(newCustomerId);
            toastService.success(th('warranty.customerDeclaration.repairClose.discountRatesUpdated') || 'İndirim oranları güncellendi');
        } else {
            setCurrentDiscountRate(0);
            // Reset discounts when customer is cleared
            setParts(prev => prev.map(part => ({
                ...part,
                discountRate: 0,
                discountedPrice: part.price
            })));
        }
    };

    // Validation state
    const [errors, setErrors] = useState({});

    const partDropdownOptions = useMemo(() => {
        const items = externalItemsData?.items ?? [];
        return items.map((item) => ({
            label: `${item.partCode} - ${item.partName}`,
            value: item.id,
            partCode: item.partCode,
            partName: item.partName,
            meins: item.meins,
            salesPrice: item.salesPrice,
            currency: item.currency
        }));
    }, [externalItemsData]);

    const handlePartFilterInputChange = (val) => {
        setPartFilterValue(val);

        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

        debounceTimerRef.current = setTimeout(() => {
            setPartSearchText(val); // <-- backend SearchText
        }, 500);
    };

    const partFilterTemplate = useMemo(() => {
        return (
            <div className="p-dropdown-filter-container">
                <InputText value={partFilterValue} onChange={(e) => handlePartFilterInputChange(e.target.value)} className="p-dropdown-filter w-full" placeholder={th('common.search')} autoFocus type="text" inputMode="text" />
                <span className="p-dropdown-filter-icon pi pi-search" />
            </div>
        );
    }, [partFilterValue, th]);

    useEffect(() => {
        if (visible) {
            refetchDetail();
        }
    }, [visible, refetchDetail]);

    const detail = detailResp?.data;

    const unitOptions = useMemo(() => (detail?.units ?? []).map((u) => ({ label: u.name, value: u.code, id: u.id })), [detail?.units]);
    const warehouseOptions = useMemo(() => (detail?.warehouses ?? []).map((w) => ({ label: w.name, value: w.id })), [detail?.warehouses]);
    const currencyOptions = useMemo(() => (detail?.currencies ?? []).map((c) => ({ label: c, value: c })), [detail?.currencies]);

    const [header, setHeader] = useState(emptyHeader);
    const [parts, setParts] = useState([]);
    
    // Helper function to calculate total from parts array (using discounted prices)
    const calculateTotalFromParts = (partsArray) => {
        if (!partsArray || partsArray.length === 0) return 0;
        return partsArray.reduce((sum, part) => {
            // Use discounted price if available, otherwise use original price
            const effectivePrice = part.discountedPrice != null ? part.discountedPrice : part.price;
            return sum + (Number(part.qty || 0) * Number(effectivePrice || 0));
        }, 0);
    };
    
    // Update invoice amount when parts change (called manually, not via useEffect)
    const recalculateInvoiceAmount = (newParts) => {
        const total = calculateTotalFromParts(newParts);
        setHeader(h => ({ ...h, invoiceAmount: total }));
    };

    // Calculate KDV'li tutar (only for display)
    const kdvliTutar = useMemo(() => {
        if (header.invoiceAmount == null || header.invoiceAmount === 0 || kdvRate == null) return null;
        const rate = parseFloat(kdvRate) || 0;
        const kdvAmount = (header.invoiceAmount * rate) / 100;
        return header.invoiceAmount + kdvAmount;
    }, [header.invoiceAmount, kdvRate]);

    // Reset selectedPartId and selectedCustomer when dialog closes   
    useEffect(() => {
        if (!visible) {
            setSelectedPartId(null);
            setSelectedCustomer(null);
            setEditCustomerId(null);
            setCustomerFromId(null);
            setCurrentDiscountRate(0);
            setCustomerSearchText('');
            setDebouncedCustomerSearch('');
            setCustomerFilterValue('');
            setErrors({});
        }
    }, [visible]);

    useEffect(() => {
        if (!visible) return;
        if (!detail) return;

        setHeader({
            invoiceNo: detail.invoiceNo ?? (isEdit ? invoiceNo : ''),
            invoiceDate: detail.invoiceDate ? new Date(detail.invoiceDate) : null,
            invoiceAmount: detail.invoiceAmount ?? null,
            wareHouseId: detail.selectedWarehouseId ?? null,
            currency: detail.currency ?? null
        });

        // Set customer from API data (edit mode) - trigger fetch by ID
        if (detail.customerId != null) {
            setEditCustomerId(detail.customerId); // This will trigger useGetCustomerByIdQuery
            // selectedCustomer will be set after customerByIdData arrives
        }

        const findUnitByLabel = (unitText) => {
            if (!unitOptions.length) return null;
            if (!unitText) return unitOptions[0]?.value ?? null;
            const found = unitOptions.find((opt) => opt.label === unitText || opt.value === unitText);
            return found?.value ?? unitOptions[0]?.value ?? null;
        };

        const mapped = (detail.parts ?? []).map((p) => ({
            name: p.partName ?? '',
            code: p.partCode ?? '',
            qty: p.quantity ?? 1,
            price: p.unitPrice ?? 0,
            unit: findUnitByLabel(p.unit),
            shelf: p.shelf ?? '',
            eyes: p.eyes ?? ''
        }));
        setParts(mapped);
    }, [detail, invoiceNo, isEdit, visible, unitOptions]);

    const handlePartSelect = (partId) => {
        if (!partId) return;

        const selectedItem = partDropdownOptions.find((p) => p.value === partId);
        if (!selectedItem) return;

        // Find matching unit from unitOptions based on meins
        const findUnitByMeins = (meins) => {
            if (!unitOptions.length) return null;
            if (!meins) return unitOptions[0]?.value ?? null;
            const found = unitOptions.find((opt) => opt.label === meins || opt.value === meins);
            return found?.value ?? unitOptions[0]?.value ?? null;
        };

        const originalPrice = selectedItem.salesPrice ?? 0;
        const newPart = {
            name: selectedItem.partName,
            code: selectedItem.partCode,
            qty: 1,
            price: originalPrice,
            unit: findUnitByMeins(selectedItem.meins),
            isFromDropdown: true,
            discountRate: currentDiscountRate,
            discountedPrice: calculateDiscountedPrice(originalPrice, currentDiscountRate),
            shelf: selectedItem.shelf ?? '',
            eyes: selectedItem.eyes ?? ''
        };

        setParts((prev) => {
            const newParts = [...prev, newPart];
            recalculateInvoiceAmount(newParts);
            return newParts;
        });
        setSelectedPartId(null);
        setPartFilterValue('');
        setPartSearchText('');
        setErrors(prev => ({ ...prev, parts: null }));
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };

    const removePart = (index) => {
        setParts((prev) => {
            const newParts = prev.filter((_, i) => i !== index);
            recalculateInvoiceAmount(newParts);
            return newParts;
        });
    };

    const updatePart = (index, field, value) => {
        setParts((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            
            // Recalculate discounted price when price or discount rate changes
            if (field === 'price') {
                next[index].discountedPrice = calculateDiscountedPrice(value, next[index].discountRate || 0);
            }
            if (field === 'discountRate') {
                next[index].discountedPrice = calculateDiscountedPrice(next[index].price, value);
            }
            
            // Recalculate total when qty, price, or discount changes
            if (field === 'qty' || field === 'price' || field === 'discountRate') {
                recalculateInvoiceAmount(next);
            }
            return next;
        });
    };

    const partUnitEditor = (rowData, options) => <Dropdown value={rowData.unit} options={unitOptions} onChange={(e) => updatePart(options.rowIndex, 'unit', e.value)} placeholder="Seçiniz" className="w-full" />;

    const partNameEditor = (rowData, options) => <InputText value={rowData.name} onChange={(e) => updatePart(options.rowIndex, 'name', e.target.value)} disabled={rowData.isFromDropdown} className={rowData.isFromDropdown ? 'p-disabled' : ''} />;

    const partCodeEditor = (rowData, options) => <InputText value={rowData.code} onChange={(e) => updatePart(options.rowIndex, 'code', e.target.value)} disabled={rowData.isFromDropdown} className={rowData.isFromDropdown ? 'p-disabled' : ''} />;

    const qtyEditor = (rowData, options) => {
        // Check if unit is ADT (adet) - case insensitive
        const isAdt = rowData.unit && rowData.unit.toString().toUpperCase() === 'ADT';
        
        return isAdt ? (
            <InputNumber 
                value={rowData.qty} 
                onValueChange={(e) => updatePart(options.rowIndex, 'qty', e.value ?? 0)} 
                min={0} 
                inputClassName="w-full" 
                className="w-full" 
            />
        ) : (
            <InputNumber 
                value={rowData.qty} 
                onValueChange={(e) => updatePart(options.rowIndex, 'qty', e.value ?? 0)} 
                min={0}
                mode="decimal"
                maxFractionDigits={2}
                locale={browserLocale}
                inputClassName="w-full" 
                className="w-full" 
            />
        );
    };

    const priceEditor = (rowData, options) => (
        <InputNumber value={rowData.price} onValueChange={(e) => updatePart(options.rowIndex, 'price', e.value ?? 0)} min={0} mode="decimal" maxFractionDigits={2} locale={browserLocale} inputClassName="w-full" className="w-full" />
    );

    const discountRateEditor = (rowData, options) => (
        <InputNumber 
            value={rowData.discountRate ?? 0} 
            onValueChange={(e) => updatePart(options.rowIndex, 'discountRate', e.value ?? 0)} 
            min={0} 
            max={100}
            mode="decimal" 
            maxFractionDigits={2}
            locale={browserLocale}
            inputClassName="w-full" 
            className="w-full" 
        />
    );

    const discountedPriceBody = (rowData) => (
        <span className={rowData.discountRate > 0 ? 'text-green-600 font-semibold' : ''}>
            {(rowData.discountedPrice ?? rowData.price ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
    );

    // Validation function
    const validateForm = () => {
        const newErrors = {};

        if (!header.invoiceNo || header.invoiceNo.trim() === '') {
            newErrors.invoiceNo = th('validation.invoiceNoRequired');
        }
        if (!header.invoiceDate) {
            newErrors.invoiceDate = th('validation.invoiceDateRequired');
        }
        if (header.invoiceAmount == null || header.invoiceAmount === 0) {
            newErrors.invoiceAmount = th('validation.invoiceAmountRequired');
        }
        if (!header.wareHouseId) {
            newErrors.wareHouseId = th('validation.warehouseRequired');
        }
        if (!header.currency) {
            newErrors.currency = th('validation.currencyRequired');
        }
        if (!selectedCustomer) {
            newErrors.customer = th('validation.customerRequired');
        }
        if (parts.length === 0) {
            newErrors.parts = th('validation.partsRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            toastService.error(th('validation.fillRequiredFields'));
            return;
        }

        const payload = {
            invoiceSerialNumber: header.invoiceNo ?? '',
            invoiceDate: header.invoiceDate ? new Date(header.invoiceDate).toISOString() : null,
            invoiceAmount: Number(header.invoiceAmount ?? 0),
            currency: header.currency ?? null,
            wareHouseId: header.wareHouseId ?? 0,
            customerId: selectedCustomer ?? null,
            parts: parts.map((p) => ({
                partCode: p.code ?? '',
                partName: p.name ?? '',
                quantity: Number(p.qty ?? 0),
                unitPrice: Number(p.price ?? 0),
                unit: p.unit ?? null
            }))
        };

        try {
            await saveExternal(payload).unwrap();
            toastService.success(th('common.success'));
            onSuccess?.();
            onHide();
        } catch (error) {
            console.error('Save error:', error);
            toastService.error(th('customerAddressPrice.errorMessageDetailSaveFailed'));
        }
    };

    return (
        <Dialog header="Fatura Girişi" visible={visible} style={{ width: '80vw', maxWidth: 1100 }} modal onHide={onHide}>
            <div className="p-fluid grid">
                <div className="col-12 md:col-3">
                    <InputText 
                        value={header.invoiceNo ?? ''} 
                        onChange={(e) => { setHeader((h) => ({ ...h, invoiceNo: e.target.value })); setErrors(prev => ({ ...prev, invoiceNo: null })); }} 
                        placeholder={th('table.invoiceNo')} 
                        className={errors.invoiceNo ? 'p-invalid' : ''}
                    />
                    {errors.invoiceNo && <small className="p-error">{errors.invoiceNo}</small>}
                </div>
                <div className="col-12 md:col-3">
                    <Calendar 
                        value={header.invoiceDate} 
                        onChange={(e) => { setHeader((h) => ({ ...h, invoiceDate: e.value })); setErrors(prev => ({ ...prev, invoiceDate: null })); }} 
                        dateFormat="dd/mm/yy" 
                        showIcon 
                        placeholder={th('table.invoiceDate')} 
                        maxDate={new Date()} 
                        className={`w-full ${errors.invoiceDate ? 'p-invalid' : ''}`}
                    />
                    {errors.invoiceDate && <small className="p-error">{errors.invoiceDate}</small>}
                </div>
                <div className="col-12 md:col-3">
                    <InputNumber
                        value={header.invoiceAmount}
                        onValueChange={(e) => { setHeader((h) => ({ ...h, invoiceAmount: e.value ?? 0 })); setErrors(prev => ({ ...prev, invoiceAmount: null })); }}
                        min={0}
                        mode="decimal"
                        maxFractionDigits={2}
                        locale={browserLocale}
                        className={`w-full ${errors.invoiceAmount ? 'p-invalid' : ''}`}
                        inputClassName="w-full"
                        placeholder={th('table.totalAmount')}
                    />
                    {errors.invoiceAmount && <small className="p-error">{errors.invoiceAmount}</small>}
                </div>
                <div className="col-12 md:col-3">
                    <Dropdown 
                        value={header.wareHouseId} 
                        options={warehouseOptions} 
                        onChange={(e) => { setHeader((h) => ({ ...h, wareHouseId: e.value })); setErrors(prev => ({ ...prev, wareHouseId: null })); }} 
                        placeholder={th('dealerStockReport.warehouse')} 
                        className={`w-full ${errors.wareHouseId ? 'p-invalid' : ''}`}
                    />
                    {errors.wareHouseId && <small className="p-error">{errors.wareHouseId}</small>}
                </div>
                <div className="col-12 md:col-3">
                    <Dropdown 
                        value={header.currency} 
                        options={currencyOptions} 
                        onChange={(e) => { setHeader((h) => ({ ...h, currency: e.value })); setErrors(prev => ({ ...prev, currency: null })); }} 
                        placeholder={th('product.currency')} 
                        className={`w-full ${errors.currency ? 'p-invalid' : ''}`}
                    />
                    {errors.currency && <small className="p-error">{errors.currency}</small>}
                </div>
                <div className="col-12 md:col-3">
                    <Dropdown
                        value={selectedCustomer}
                        options={customerOptions}
                        onChange={(e) => handleCustomerChange(e.value)}
                        onFilter={(e) => {
                            setCustomerFilterValue(e.filter);
                            setCustomerSearchText(e.filter);
                        }}
                        filterValue={customerFilterValue}
                        optionLabel="label"
                        optionValue="value"
                        placeholder={th('preAccounting.customer')}
                        filter
                        showClear
                        resetFilterOnHide={false}
                        className={`w-full ${errors.customer ? 'p-invalid' : ''}`}
                        emptyMessage={th('common.noRecords')}
                        emptyFilterMessage={th('common.noRecords')}
                    />
                    {errors.customer && <small className="p-error">{errors.customer}</small>}
                </div>
            </div>

            <div className="mt-3">
                <div className="mb-2 flex gap-2 align-items-center">
                    <Dropdown
                        value={selectedPartId}
                        options={partDropdownOptions}
                        onChange={(e) => {
                            setSelectedPartId(e.value);
                            handlePartSelect(e.value);
                        }}
                        optionLabel="label"
                        optionValue="value"
                        placeholder={th('parts.selectPart')}
                        showClear
                        filter
                        filterTemplate={partFilterTemplate} // <-- kritik
                        className="w-20rem"
                        loading={isFetchingParts} // <-- güzel UX
                        emptyMessage={th('common.noRecords')}
                        emptyFilterMessage={th('common.noRecords')}
                        panelStyle={{ maxWidth: '600px' }}
                    />
                </div>

                {errors.parts && <small className="p-error mb-2 block">{errors.parts}</small>}
                <DataTable value={parts} dataKey="__idx" emptyMessage={th('common.noRecords')}>
                    <Column header="#" body={(row, opts) => opts.rowIndex + 1} style={{ width: 50 }} />
                    <Column header={th('product.code')} body={partCodeEditor} style={{ width: 140 }} />
                    <Column header={th('product.name')} body={partNameEditor} />
                    <Column header={th('parts.shelf')} body={(row) => row.shelf || ''} style={{ width: 90 }} />
                    <Column header={th('parts.eyes')} body={(row) => row.eyes || ''} style={{ width: 90 }} />
                    <Column header={th('product.quantity')} body={qtyEditor} style={{ width: 100 }} />
                    <Column header={th('basket.price')} body={priceEditor} style={{ width: 130 }} />
                    <Column header={th('retailSales.discountRate') || 'İndirim %'} body={discountRateEditor} style={{ width: 110, backgroundColor: '#f0f8ff' }} />
                    <Column header={th('retailSales.sellingPrice') || 'Net Fiyat'} body={discountedPriceBody} style={{ width: 110 }} />
                    <Column header={th('parts.unit')} body={partUnitEditor} style={{ width: 120 }} />
                    <Column header="" style={{ width: 60 }} body={(row, opts) => <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => removePart(opts.rowIndex)} />} />
                </DataTable>
            </div>

            <div className="flex justify-content-between align-items-center mt-4">
                {kdvliTutar != null ? (
                    <div className="flex align-items-center gap-2 p-2 surface-100 border-round">
                        <span className="font-semibold text-sm">
                            {th('preAccounting.kdvliTutar')} ({kdvRate}% KDV):
                        </span>
                        <span className="text-lg font-bold text-primary">
                            {kdvliTutar.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {header.currency || ''}
                        </span>
                    </div>
                ) : (
                    <div />
                )}
                <div className="flex gap-2">
                    <Button label={th('common.save')} icon="pi pi-save" severity="success" onClick={handleSave} loading={isSaving} />
                    <Button label={th('common.cancel')} icon="pi pi-times" severity="secondary" onClick={onHide} />
                </div>
            </div>
        </Dialog>
    );
}
